import { ReactNode, useCallback } from 'react'
import { toast } from 'react-toastify'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { ORDContragentRole } from '@synapex/prisma/client'
import { ContragentResponse } from '@synapex/shared2/gateway-rest-api'
import { WithOptional } from '@synapex/tools/typescript'
import { cn } from '@/true-fsd/shared/utils/css'
import {
    Dialog,
    DialogClose,
    DialogContent,
    type DialogContentProps,
    DialogHeader,
    type DialogProps,
    DialogTitle,
} from '@/shared/ui'

import {
    ContragentCreateForm,
    ContragentCreateFormProps,
    ContragentUpdateForm,
    ContragentUpdateFormProps,
} from './ContragentForm'

type ContragentModalProps = Pick<DialogProps, 'open' | 'onOpenChange'> &
    Pick<DialogContentProps, 'overlayStyle'> & {
        title: string
        children: ReactNode
        roleType: ORDContragentRole
    }
const ContragentModal = ({
    title,
    open,
    onOpenChange,
    overlayStyle,
    children,
    roleType,
}: ContragentModalProps) => {
    const contentPosition =
        roleType === 'publisher'
            ? 'right-12 left-auto translate-x-0 top-28 translate-y-0'
            : ''

    return (
        <Dialog {...{ open, onOpenChange }}>
            <DialogContent
                overlayStyle={overlayStyle}
                aria-describedby={undefined}
                className={cn(
                    'flex max-h-[85vh] flex-col overflow-hidden bg-white px-10 py-[30px] sm:rounded-2xl',
                    contentPosition
                )}
            >
                <div
                    style={{
                        scrollbarWidth: 'thin',
                    }}
                    className="-mr-2 flex-1 overflow-y-scroll pr-2"
                >
                    <DialogHeader className="mb-5 items-start">
                        <DialogTitle className="text-xl font-semibold">
                            {title}
                        </DialogTitle>
                    </DialogHeader>
                    {children}
                </div>
            </DialogContent>
        </Dialog>
    )
}

const CloseButton = () => (
    <DialogClose className="w-full rounded-lg border-[1.5px] border-blue-primary bg-white px-4 py-2 text-[13px] text-blue-primary">
        Отмена
    </DialogClose>
)

const ROLE_TITLES: Record<ORDContragentRole, { create: string; edit: string }> =
    {
        publisher: {
            create: 'Добавить реквизиты площадки',
            edit: 'Редактировать реквизиты площадки',
        },
        advertiser: {
            create: 'Добавить рекламодателя',
            edit: 'Редактировать рекламодателя',
        },
        agency: {
            create: 'Добавить рекламное агентство',
            edit: 'Редактировать рекламное агентство',
        },
        ors: { create: 'Добавить ОРС', edit: 'Редактировать ОРС' },
    }

export type ContragentCreateModalProps = Omit<
    ContragentModalProps,
    'children' | 'title'
> &
    WithOptional<WorkspaceWithLinkDescriptor, 'link_id'> & {
        roleType?: ORDContragentRole
    }
export const ContragentCreateModal = ({
    workspace_type,
    workspace_id,
    link_id,
    onOpenChange,
    roleType = 'publisher',
    ...modalProps
}: ContragentCreateModalProps) => {
    const onSubmitted: ContragentCreateFormProps['onSubmitted'] = useCallback(
        ({ status }) => {
            switch (status) {
                case 'error':
                    toast.error('Не удалось создать контрагента')
                    break
                case 'success':
                    toast.info('Контрагент успешно создан')
                    if (onOpenChange) {
                        onOpenChange(false)
                    }
                    break
            }
        },
        [onOpenChange]
    )

    return (
        <ContragentModal
            {...modalProps}
            {...{ onOpenChange }}
            title={ROLE_TITLES[roleType].create}
            roleType={roleType}
        >
            <ContragentCreateForm
                {...{
                    workspace_type,
                    workspace_id,
                    link_id,
                    onSubmitted,
                    roleType,
                }}
                slotExtraButtons={<CloseButton />}
            />
        </ContragentModal>
    )
}

export type ContragentUpdateModalProps = {
    source: ContragentResponse
    roleType?: ORDContragentRole
} & Omit<ContragentModalProps, 'children' | 'title'> &
    WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
export const ContragentUpdateModal = ({
    source,
    workspace_type,
    workspace_id,
    link_id,
    onOpenChange,
    roleType = 'publisher',
    ...modalProps
}: ContragentUpdateModalProps) => {
    const onSubmitted: ContragentUpdateFormProps['onSubmitted'] = useCallback(
        ({ status }) => {
            switch (status) {
                case 'error':
                    toast.error('Не удалось обновить контрагента')
                    break
                case 'success':
                    toast.info('Контрагент успешно обновлен')
                    if (onOpenChange) {
                        onOpenChange(false)
                    }
                    break
                case 'not_changed':
                    toast.info('Нет изменений для сохранения')
                    if (onOpenChange) {
                        onOpenChange(false)
                    }
                    break
            }
        },
        [onOpenChange]
    )

    return (
        <ContragentModal
            {...modalProps}
            {...{ onOpenChange }}
            title={ROLE_TITLES[roleType].edit}
            roleType={roleType}
        >
            <ContragentUpdateForm
                {...{
                    workspace_type,
                    workspace_id,
                    link_id,
                    source,
                    onSubmitted,
                    roleType,
                }}
                slotExtraButtons={<CloseButton />}
            />
        </ContragentModal>
    )
}
